import { capitalize } from "../utils"

const ERROR_KEYS = ["_error", "detail"]

export type ErrorContext = {
  [key: string]: ErrorContext | ErrorContext[] | string | null
}

export type ResponseError =
  | {
      [key: string]: string | null | boolean
    }
  | boolean
  | string

export type ApiResponseError = {
  data: unknown
  formError: ResponseError
  message: string
  status: number
  errorCode: string
  url: string
}

/**
 * Will convert error structure from API to structure compatible with react-hook-form.
 * If there is a general error not related to the form,
 * the `_parseErrors` function will return an object with a `_error` property that contains the general error message.
 *
 * Example inputs:
 *
 * 1. `{"info":{"_error":["This field may not be blank."]}}`
 * 2. `{"info":{"company":["This field may not be blank."]}}`
 * 3. `{"detail":"Unsupported media type \"text/plain;charset=UTF-8\" in request."}`
 * Example outputs:
 *
 * 1. `{"info":"This field may not be blank."}`
 * 2. `{"info.company":"This field may not be blank."}`
 * 3. `{"_error":"Unsupported media type \"text/plain;charset=UTF-8\" in request."}`
 * @param {unknown} error Error returned from API
 * @returns {ResponseError | false} - The response error object or false.
 */

export const parseApiError = (error: unknown): ResponseError => {
  const parsedError = _parseErrors(error)

  if (isResponseError(parsedError)) {
    return parsedError
  }

  return false
}

const isResponseError = (
  error: ResponseError | ErrorContext,
): error is ResponseError => getObjectDepth(error) < 2

/**
 * Parses errors in an error object.
 * @param {unknown} error - The error object.
 * @returns {ErrorContext | string | false} - The error context object or a string or false.
 */

function _parseErrors(error: unknown): ErrorContext | string | boolean {
  if (!error) {
    return false
  }

  const errors: ErrorContext = {}

  if (Array.isArray(error)) {
    if (error.length > 1) {
      return error.join(", ")
    } else if (error.length === 1) {
      error = error[0]
    } else {
      return false
    }
  }

  Object.keys(error as ErrorContext).forEach((key) => {
    const value = (error as Record<string, unknown>)[key]
    if (typeof value === "string") {
      if (ERROR_KEYS.includes(key)) {
        errors[ERROR_KEYS[0]] = value
      } else {
        errors[key] = value
      }
    } else if (Array.isArray(value)) {
      if (value.length > 1) {
        value.forEach((subValue, subIndex) => {
          if (typeof subValue === "object" && !Array.isArray(subValue)) {
            Object.entries(subValue).forEach(([fieldKey, fieldValue]) => {
              const errorKey = `${key}.${subIndex}.${fieldKey}`

              if (typeof fieldValue === "string") {
                errors[errorKey] = fieldValue
              } else if (Array.isArray(fieldValue)) {
                errors[errorKey] = fieldValue.join(", ")
              }
            })
          }
        })

        const nonObjectValues = value.filter(
          (v) => typeof v !== "object" && String(v).trim() !== "",
        )
        if (nonObjectValues.length) {
          errors[key] = nonObjectValues
            .map((v) => capitalize(String(v).trim()))
            .join(". ")
        }
      } else {
        errors[key] = value.join(", ")
      }
    } else if (typeof value === "object") {
      const subErrors = _parseErrors(value)
      Object.keys(subErrors).forEach((subKey) => {
        if (typeof subErrors === "object") {
          // if subKey is _error it will remove that key and apply string directly to parent property
          if (
            subKey === ERROR_KEYS[0] &&
            typeof subErrors[subKey] === "string"
          ) {
            errors[key] = subErrors[subKey]
          } else {
            errors[`${key}.${subKey}`] = subErrors[subKey]
          }
        }
      })
    }
  })

  return errors
}

/**
 * Gets the depth of an object.
 * @param {object | string | boolean | number | null} o - The object to get the depth of.
 * @returns {number} - The depth of the object.
 */
const getObjectDepth = (
  o: object | string | boolean | number | null,
): number =>
  Object(o) === o
    ? 1 + Math.max(-1, ...Object.values(o as object).map(getObjectDepth))
    : 0

type QueryLoadingState = {
  isFetching: boolean
  currentData?: unknown
}

/**
 * RTK Query holds on to a cache entry's data while it refetches, so `isFetching`
 * alone puts a skeleton over content that is already on screen every time a
 * mutation invalidates the tag it provides. Only a fetch with nothing cached for
 * the arguments in play — a first load, or a move to a day or a filter that was
 * never loaded — has nothing left to show.
 */
export const isInitialLoad = ({ isFetching, currentData }: QueryLoadingState) =>
  isFetching && currentData === undefined

export const getErrorCode = (response: unknown) =>
  response &&
  typeof response === "object" &&
  "error" in response &&
  response.error &&
  typeof response.error === "object" &&
  "data" in response.error &&
  response.error.data &&
  typeof response.error.data === "object" &&
  "_error_code" in response.error.data &&
  typeof response.error.data._error_code === "string"
    ? response.error.data._error_code
    : ""

/**
 * Parses DRF-style `next` pagination URLs (absolute or relative) into an offset
 * page param for RTK infinite queries.
 */
export const getNextOffsetFromUrl = (
  next: string | null | undefined,
): number | undefined => {
  if (!next) {
    return undefined
  }

  try {
    const url = next.startsWith("http")
      ? new URL(next)
      : new URL(next, "https://placeholder.local")
    const offsetParam = url.searchParams.get("offset")
    if (offsetParam == null) {
      return undefined
    }

    const nextOffset = Number(offsetParam)

    return Number.isNaN(nextOffset) ? undefined : nextOffset
  } catch {
    return undefined
  }
}

/**
 * First page can be oversized (e.g. select-all needs every row that fits the
 * current cap); later pages keep the normal page size. `offset === 0` is the
 * first page for offset-based infinite feeds.
 */
export const resolveInfiniteFeedPageLimit = (
  offset: number,
  limit: number,
  initialLimit?: number,
): number => {
  if (offset === 0 && initialLimit != null) {
    return initialLimit
  }

  return limit
}

/**
 * Next offset after concatenating loaded pages, or undefined when the server
 * count says there is nothing left to fetch (or the last page was empty).
 */
export const getNextOffsetFromLoadedPages = (
  pages: ReadonlyArray<{ results: ReadonlyArray<unknown>; count: number }>,
): number | undefined => {
  const lastPage = pages[pages.length - 1]

  if (!lastPage || lastPage.results.length === 0) {
    return undefined
  }

  const loadedCount = pages.reduce((sum, page) => sum + page.results.length, 0)
  const totalCount = pages[0]?.count ?? 0

  if (totalCount === 0 || loadedCount >= totalCount) {
    return undefined
  }

  return loadedCount
}
